import { Maybe, WorkingHours, WorkingHoursRange } from "@hubcapp/graphql/types";
import { WorkingHour } from "@hubcapp/types";
import {
  convertMinsSinceMidnightTo24HourTime,
  convertMinsfrom24HourTimeToSinceMidnight,
} from "@hubcapp/components/Cards/MyWorkingHours/utils";

export const addPlaceholderDaysAndSort = (
  uiWorkingHours: WorkingHours,
): WorkingHours => {
  if (!uiWorkingHours.hasOwnProperty("monday")) {
    uiWorkingHours.monday = null;
  }

  if (!uiWorkingHours.hasOwnProperty("tuesday")) {
    uiWorkingHours.tuesday = null;
  }

  if (!uiWorkingHours.hasOwnProperty("wednesday")) {
    uiWorkingHours.wednesday = null;
  }

  if (!uiWorkingHours.hasOwnProperty("thursday")) {
    uiWorkingHours.thursday = null;
  }

  if (!uiWorkingHours.hasOwnProperty("friday")) {
    uiWorkingHours.friday = null;
  }

  if (!uiWorkingHours.hasOwnProperty("saturday")) {
    uiWorkingHours.saturday = null;
  }

  if (!uiWorkingHours.hasOwnProperty("sunday")) {
    uiWorkingHours.sunday = null;
  }

  return {
    monday: uiWorkingHours.monday,
    tuesday: uiWorkingHours.tuesday,
    wednesday: uiWorkingHours.wednesday,
    thursday: uiWorkingHours.thursday,
    friday: uiWorkingHours.friday,
    saturday: uiWorkingHours.saturday,
    sunday: uiWorkingHours.sunday,
  };
};

export const gatewayHoursToUiHours = (
  workingHours: Array<WorkingHour>,
): {
  monday?: WorkingHoursRange;
  tuesday?: WorkingHoursRange;
  wednesday?: WorkingHoursRange;
  thusday?: WorkingHoursRange;
  friday?: WorkingHoursRange;
  saturday?: WorkingHoursRange;
  sunday?: WorkingHoursRange;
} =>
  Object.assign(
    {},
    ...workingHours.map((workingHour: WorkingHour) => ({
      [workingHour.day.toLowerCase()]:
        workingHour.start && workingHour.end
          ? {
              from: convertMinsfrom24HourTimeToSinceMidnight(
                workingHour.start || "",
              ),
              to: convertMinsfrom24HourTimeToSinceMidnight(
                workingHour.end || "",
              ),
            }
          : null,
    })),
  );

// eslint-disable-next-line
const getKeyValue = <T extends object, U extends keyof T>(obj: T) => (key: U) =>
  obj[key];

const dayNumberMap: { [key: string]: number } = {
  monday: 0,
  tuesday: 1,
  wednesday: 2,
  thursday: 3,
  friday: 4,
  saturday: 5,
  sunday: 6,
};

export const uiHoursToGatewayHours = (
  workingHours?: Record<string, Maybe<{ from: string; to: string }>>,
): Array<{
  dayNumber: number;
  startTime: string | null;
  endTime: string | null;
}> => {
  if (!workingHours) {
    // In the scenario when no working hours are passed through, create a working hour set for each day with a start/end of null
    const dayNumbers = Array.from(Array(7).keys());
    const emptyWorkingHours = dayNumbers.map(dayNumber => ({
      dayNumber,
      startTime: null,
      endTime: null,
    }));
    return emptyWorkingHours;
  }

  const updatedWorkingHours = Object.keys(workingHours)
    .map((key: string) => {
      const workingHourItem = getKeyValue(workingHours)(key);
      // If the working hour is null in the object keyed by the day, then set its start/end in the array as null
      if (!workingHourItem) {
        return {
          dayNumber: dayNumberMap[key.toLowerCase()],
          startTime: null,
          endTime: null,
        };
      }

      if (!workingHourItem.from && !workingHourItem.to) {
        return {
          dayNumber: dayNumberMap[key.toLowerCase()],
          startTime: null,
          endTime: null,
        };
      }

      return {
        dayNumber: dayNumberMap[key.toLowerCase()],
        startTime: convertMinsSinceMidnightTo24HourTime(
          parseInt(workingHourItem.from),
          { format: true },
        ),
        endTime: convertMinsSinceMidnightTo24HourTime(
          parseInt(workingHourItem.to),
          { format: true },
        ),
      };
    })
    .filter(Boolean) as Array<{
    dayNumber: number;
    startTime: string | null;
    endTime: string | null;
  }>;

  return updatedWorkingHours;
};
